"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, MapPin, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const coreValues = [{
  title: "Neighborhood experience",
  description: "Deep zoning, historic, and structural familiarity across Brookhaven, Buckhead, and Dunwoody."
}, {
  title: "Transparent milestones",
  description: "Firm scheduling with clear weekly site updates and itemized project budgets from day one."
}, {
  title: "Bespoke execution",
  description: "Project coordinators on site guiding our trades team."
}];
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-story" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-5 sm:px-9 lg:px-16 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Narrative Column (5 Columns) */}
          <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs uppercase tracking-widest font-semibold text-primary">
                Local craftsmanship &amp; roots
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-[2.65rem] font-serif font-semibold text-foreground tracking-tight leading-[1.2]">
              About Brookhaven Elevation Renovations
            </h1>

            {/* Architectural Brushed Gold Hairline Divider */}
            <div className="w-16 h-[2px] bg-primary/60 my-6" />

            <div className="space-y-4 text-foreground/90 font-sans text-base sm:text-lg leading-relaxed">
              <p>
                Founded right here on Peachtree Dunwoody Road, Brookhaven Elevation Renovations was built to give North Atlanta homeowners an honest, design-led remodeling experience. We live in these neighborhoods, navigate the local permitting offices weekly, and understand the architectural character of classic and modern Georgia homes.
              </p>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Whether opening up a dated compartmentalized kitchen, curating a spa-level primary suite, or building seamless outdoor living additions, our work balances enduring structural craftsmanship with daily living comfort. You get one dedicated project coordinator, realistic timelines, and clear weekly communication from design consultation to post-remodel warranty.
              </p>
            </div>

            {/* Core Values / Neighborhood Pillars */}
            <div className="mt-8 space-y-4 pt-6 border-t border-border/70">
              {coreValues.map((value, i) => <div key={value.title} data-index={i} className="flex items-start gap-3.5">
                  <div className="p-1 rounded-full bg-primary/10 text-primary mt-1 shrink-0">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      {value.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-normal">
                      {value.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Local communities served pill row */}
            <div className="mt-8 pt-6 border-t border-border/70">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                Serving our local communities
              </span>
              <div className="flex flex-wrap gap-2">
                {["Brookhaven", "Buckhead", "Dunwoody", "Sandy Springs", "Chamblee", "Doraville"].map((community, i) => <Badge key={community} data-index={i} variant="outline" className="bg-card text-foreground border-border text-xs px-2.5 py-1 rounded-md font-normal">
                      <MapPin className="size-3 text-primary mr-1 inline" />
                      {community}
                    </Badge>)}
              </div>
            </div>
          </motion.div>

          {/* Right Image Container Column (7 Columns) */}
          <motion.div variants={itemVariants} className="lg:col-span-7 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border/70 bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_craftsmanship.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_craftsmanship.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_craftsmanship.png-wvc-sizes" alt="Residential remodeling craftsman fine-tuning custom white oak cabinetry in a Brookhaven Georgia residence" loading="lazy" decoding="async" className="w-full h-[400px] sm:h-[480px] lg:h-[580px] object-cover object-center" />

              {/* Atelier Frosted Glass Overlay Badge */}
              <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-md sm:bottom-6 sm:left-6">
                <motion.div whileHover={shouldReduceMotion ? {} : {
                y: -4
              }} className="rounded-2xl bg-card/85 backdrop-blur-md border border-card/90 shadow-2xl p-4 sm:p-5 transition-[border-color,box-shadow] duration-300 hover:border-primary/50">
                  <div className="flex items-center gap-3.5 sm:gap-4">
                    <div className="size-11 sm:size-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0 border border-primary/25 text-primary">
                      <ShieldCheck className="size-5 sm:size-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Backed by our craftsmanship standards, on-site project coordinators, and experienced local trades.
                      </p>
                    </div>
                  </div>

                  <div className="mt-3.5 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium text-foreground">
                      <Award className="size-3.5 text-primary" />
                      Brookhaven, GA Based Team
                    </span>
                    <Link to="/ContactRequestAnEstimatePage" className="font-medium text-primary hover:underline">
                      Connect with builders
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}